import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

type Props = {
    nickname: string;
    isDisabledDueToModeration: boolean;
    isAIGuideEnabled: boolean;
    setAIGuideEnabled: (state: boolean) => void;
    isAIGuideEnabledForParent: boolean;
    isChild: boolean;
    includesDistrictOwnedData: boolean;
    canEnableChildren: boolean;
    guideChildLimitReached?: boolean;
};

type DisabledReason = {
    description: string;
    icon: PhosphorBold;
    textColor: string;
};

export default function AIGuideUserEntry({
    nickname,
    isDisabledDueToModeration,
    isAIGuideEnabled,
    setAIGuideEnabled,
    isAIGuideEnabledForParent,
    isChild,
    includesDistrictOwnedData,
    canEnableChildren,
    guideChildLimitReached = false,
}: Props): React.ReactElement {
    const isChecked =
        (isAIGuideEnabledForParent && isAIGuideEnabled) ||
        (isChild && includesDistrictOwnedData && isAIGuideEnabled);

    const [disabledReason, setDisabledReason] =
        React.useState<DisabledReason | null>(null);

    const checkboxDisabled = React.useMemo(
        () => !!disabledReason,
        [disabledReason],
    );

    // Set reason for disabled checkbox
    React.useEffect(() => {
        // Parent does not have Khanmigo
        if (!isAIGuideEnabledForParent) {
            setDisabledReason({
                description: t`You do not have Khanmigo enabled.`,
                icon: infoIcon,
                textColor: color.offBlack50,
            });
            return;
        }
        // Parent's Khanmigo access has been disabled via moderation
        if (isDisabledDueToModeration) {
            setDisabledReason({
                description: t`Disabled due to moderation. For more information please review our community guidelines.`,
                icon: infoIcon,
                textColor: color.red,
            });
            return;
        }
        // Child access is managed by district
        if (isChild && includesDistrictOwnedData) {
            setDisabledReason({
                description: t`Khanmigo access is controlled by this student's district`,
                icon: infoIcon,
                textColor: color.offBlack50,
            });
            return;
        }
        // Parent is unable to give children access
        if (isChild && !canEnableChildren && !isChecked) {
            setDisabledReason({
                description: t`Your current Khanmigo plan does not allow you to enable Khanmigo access for your child.`,
                icon: infoIcon,
                textColor: color.red,
            });
            return;
        }
        // Parent has reached the limit for KM-enabled children
        if (isChild && guideChildLimitReached && !isChecked) {
            setDisabledReason({
                description: t`You have reached the limit for Khanmigo-enabled children.`,
                icon: infoIcon,
                textColor: color.red,
            });
            return;
        }
        setDisabledReason(null);
    }, [
        isAIGuideEnabledForParent,
        isDisabledDueToModeration,
        isChild,
        includesDistrictOwnedData,
        canEnableChildren,
        isChecked,
        guideChildLimitReached,
    ]);

    const toggleCheckbox = () => {
        if (!checkboxDisabled) {
            setAIGuideEnabled(!isAIGuideEnabled);
        }
    };

    return (
        <View style={styles.userEntry}>
            <Checkbox
                aria-disabled={checkboxDisabled}
                onChange={toggleCheckbox}
                checked={isChecked}
                style={
                    checkboxDisabled ? styles.checkboxDisabled : styles.checkbox
                }
                label={t`Enable Khanmigo for ${nickname}.`}
            />
            <Strut size={spacing.small_12} />
            {disabledReason && (
                <Tooltip content={disabledReason.description}>
                    <PhosphorIcon
                        size="small"
                        icon={disabledReason.icon}
                        color={disabledReason.textColor}
                        aria-label={disabledReason.description}
                    />
                </Tooltip>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    userEntry: {
        flexDirection: "row",
        verticalAlign: "baseline",
    },
    checkbox: {
        marginTop: 2,
    },
    checkboxDisabled: {
        marginTop: 2,
        color: color.offBlack32,
    },
});
